import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import {
  useProduct,
  useFeaturedProducts,
  useProductVariants,
} from "@/hooks/useProducts";
import {
  getShopName,
  getRating,
  currencySymbol,
  formatPrice,
  getInstallment,
} from "@/pages/home/components/ProductCard";
import { useShop } from "@/store/ShopContext";
import ProductGallery from "./components/ProductGallery";
import RelatedProducts from "./components/RelatedProducts";
import ReviewsBlock from "./components/ReviewsBlock";
import VariantSelector from "./components/VariantSelector";
import RecentlyViewed from "./components/RecentlyViewed";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : null;
  const { product, loading, error } = useProduct(numericId);
  const { variants, skus, loading: optsLoading } = useProductVariants(numericId);
  const { isFavorite, toggleFavorite, addToCart, openCart, showToast } = useShop();

  const { products: related } = useFeaturedProducts({
    limit: 4,
    categoryId: product?.category_id ?? null,
    excludeId: product?.id ?? null,
  });

  const [qty, setQty] = useState(1);
  const [activeTab, setActiveTab] = useState<"about" | "shipping" | "returns">("about");

  const { recent, track, clear: clearRecent } = useRecentlyViewed();
  useEffect(() => {
    if (product?.id != null) {
      track(product.id);
    }
  }, [product?.id, track]);
  const recentExcludingCurrent = useMemo(
    () => recent.filter((id) => id !== product?.id),
    [recent, product?.id],
  );

  const [selected, setSelected] = useState<(string | null)[]>([]);
  useEffect(() => {
    setSelected(new Array(variants.length).fill(null));
    setQty(1);
  }, [variants]);

  const matchedSku = useMemo(() => {
    if (variants.length === 0) return null;
    if (selected.some((v) => v == null)) return null;
    return (
      skus.find((sku) => {
        const opts = sku.options ?? [];
        return selected.every((val, i) => opts[i] === val);
      }) ?? null
    );
  }, [variants, skus, selected]);

  const allOptionsChosen =
    variants.length === 0 || selected.every((v) => v != null);

  const priceInfo = useMemo(() => {
    if (!product) return null;
    const useSku = matchedSku && product.pricing_mode === 1;
    const basePrice = useSku
      ? Number(matchedSku!.price ?? product.price ?? 0)
      : product.price != null
      ? Number(product.price)
      : 0;
    const skuDiscEnabled = !!matchedSku?.discount_enabled;
    const skuDiscPrice = matchedSku?.discount_price != null ? Number(matchedSku.discount_price) : 0;
    const prodDiscEnabled = !!product.discount_enabled;
    const prodDiscPrice = product.discount_price != null ? Number(product.discount_price) : 0;
    const discEnabled = useSku ? skuDiscEnabled : prodDiscEnabled;
    const discPrice = useSku ? skuDiscPrice : prodDiscPrice;
    const hasDiscount = discEnabled && discPrice > 0 && discPrice < basePrice;
    return {
      currentPrice: hasDiscount ? discPrice : basePrice,
      originalPrice: basePrice,
      hasDiscount,
      pctOff: hasDiscount ? Math.round(((basePrice - discPrice) / basePrice) * 100) : 0,
      symbol: currencySymbol(product.currency),
    };
  }, [product, matchedSku]);

  const effectiveStock = useMemo(() => {
    if (!product) return 0;
    if (variants.length > 0) {
      if (!matchedSku) {
        return skus.reduce((sum, s) => sum + Math.max(0, s.stock ?? 0), 0);
      }
      return Math.max(0, matchedSku.stock ?? 0);
    }
    return Math.max(0, product.stock ?? 0);
  }, [product, variants, matchedSku, skus]);

  const canPurchase = allOptionsChosen && effectiveStock > 0;

  useEffect(() => {
    setQty((q) => Math.max(1, Math.min(effectiveStock || 1, q)));
  }, [effectiveStock]);

  if (loading || optsLoading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-[1280px] mx-auto px-4 md:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 animate-pulse">
            <div className="aspect-square rounded-2xl bg-background-200/60"></div>
            <div className="space-y-4">
              <div className="h-4 w-1/3 bg-background-200/60 rounded"></div>
              <div className="h-8 w-3/4 bg-background-200/60 rounded"></div>
              <div className="h-6 w-1/4 bg-background-200/60 rounded"></div>
              <div className="h-24 w-full bg-background-200/60 rounded mt-6"></div>
              <div className="h-12 w-full bg-background-200/60 rounded mt-6"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <p className="font-heading text-foreground-950 text-2xl mb-3">
              Produto não encontrado
            </p>
            <p className="text-sm text-foreground-700 mb-6">
              {error ?? "Este item não está disponível no momento."}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar à loja
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const media = Array.isArray(product.media) ? product.media : [];
  const shopName = getShopName(product.id);
  const { rating, count } = getRating(product.id);
  const liked = isFavorite(product.id);
  const isLowStock = effectiveStock > 0 && effectiveStock <= 6;
  const categoryName = product.product_categories?.name ?? null;

  const decQty = () => setQty((q) => Math.max(1, q - 1));
  const incQty = () => setQty((q) => Math.min(effectiveStock || 1, q + 1));

  const onToggleFavorite = () => {
    toggleFavorite(product.id);
    showToast(liked ? "Removido dos favoritos" : "Salvo nos favoritos");
  };

  const onAddToCart = () => {
    if (!priceInfo) return;
    if (!allOptionsChosen) {
      showToast("Escolha todas as opções primeiro");
      return;
    }
    if (effectiveStock <= 0) {
      showToast("Esgotado");
      return;
    }
    const mainImg = media[0]?.url ?? "";
    addToCart({
      productId: product.id,
      productName: product.name,
      image: mainImg,
      shopName,
      skuId: matchedSku?.id ?? null,
      skuLabel: matchedSku?.label ?? null,
      options: selected.filter((v): v is string => v != null),
      unitPrice: priceInfo.currentPrice,
      originalPrice: priceInfo.originalPrice,
      currency: product.currency,
      quantity: qty,
      maxStock: effectiveStock,
    });
    openCart();
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />

      <main>
        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          className="max-w-[1280px] mx-auto px-4 md:px-8 pt-5 pb-2 text-[13px] text-foreground-600"
        >
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to="/" className="hover:underline cursor-pointer">
                Início
              </Link>
            </li>
            <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
            {categoryName && (
              <>
                <li>
                  <Link to="/" className="hover:underline cursor-pointer">
                    {categoryName}
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              </>
            )}
            <li className="text-foreground-900 line-clamp-1">{product.name}</li>
          </ol>
        </nav>

        {/* Main product block */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-8 pt-3 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Gallery */}
            <div className="lg:col-span-7">
              <ProductGallery images={media} title={product.name} />
            </div>

            {/* Buy panel */}
            <div className="lg:col-span-5">
              {priceInfo?.hasDiscount && (
                <div className="mb-3">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold tracking-wide">
                    {priceInfo.pctOff}% OFF
                  </span>
                </div>
              )}

              <p className="text-[13px] text-foreground-600 mb-1">{shopName}</p>
              <h1 className="font-heading text-foreground-950 text-2xl md:text-[28px] leading-snug">
                {product.name}
              </h1>

              <div className="mt-3 flex items-center gap-2 text-[13px] text-foreground-700">
                <span className="flex items-center gap-0.5 text-foreground-950">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`${i < Math.round(rating) ? "ri-star-fill" : "ri-star-line"} text-[14px]`}
                    ></i>
                  ))}
                </span>
                <span className="font-medium text-foreground-900">{rating.toFixed(1)}</span>
                <span className="text-foreground-500">({count} avaliações)</span>
              </div>

              {/* Price */}
              <div className="mt-5 flex items-baseline gap-3">
                <span
                  className={`text-3xl md:text-4xl font-semibold ${
                    priceInfo?.hasDiscount ? "text-primary-600" : "text-foreground-950"
                  }`}
                >
                  {priceInfo?.symbol}
                  {formatPrice(priceInfo?.currentPrice ?? 0)}
                </span>
                {priceInfo?.hasDiscount && (
                  <>
                    <span className="text-base text-foreground-500 line-through">
                      {priceInfo.symbol}
                      {formatPrice(priceInfo.originalPrice)}
                    </span>
                    <span className="text-[13px] text-primary-600 font-medium">
                      Economize {priceInfo.symbol}
                      {formatPrice(priceInfo.originalPrice - priceInfo.currentPrice)}
                    </span>
                  </>
                )}
              </div>
              {(() => {
                const inst = getInstallment(priceInfo?.currentPrice ?? 0);
                return inst ? (
                  <p className="text-[13px] text-foreground-700 mt-1.5">
                    ou <span className="font-medium text-foreground-950">{inst.count}x de {priceInfo?.symbol}{formatPrice(inst.value)}</span> sem juros
                  </p>
                ) : null;
              })()}
              <p className="text-[12px] text-foreground-500 mt-1">
                Impostos inclusos
              </p>

              {/* Stock */}
              {effectiveStock > 0 ? (
                isLowStock ? (
                  <p className="mt-4 text-[13px] text-primary-600 inline-flex items-center gap-1.5">
                    <i className="ri-fire-line text-[14px]"></i>
                    Só restam {effectiveStock} unidades
                  </p>
                ) : (
                  <p className="mt-4 text-[13px] text-foreground-700 inline-flex items-center gap-1.5">
                    <i className="ri-checkbox-circle-line text-foreground-900 text-[14px]"></i>
                    Em estoque
                  </p>
                )
              ) : allOptionsChosen ? (
                <p className="mt-4 text-[13px] text-primary-600 inline-flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  Esgotado
                </p>
              ) : null}

              {/* Variants + Quantity */}
              <div className="mt-6 space-y-5">
                {variants.length > 0 && (
                  <VariantSelector
                    variants={variants}
                    skus={skus}
                    selected={selected}
                    onChange={setSelected}
                  />
                )}

                <div>
                  <label className="block text-[13px] font-medium text-foreground-900 mb-2">
                    Quantidade
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center border border-background-300 rounded-full">
                      <button
                        type="button"
                        onClick={decQty}
                        aria-label="Diminuir quantidade"
                        className="w-10 h-10 flex items-center justify-center text-foreground-900 hover:bg-background-100 rounded-full cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={qty <= 1}
                      >
                        <i className="ri-subtract-line text-[16px]"></i>
                      </button>
                      <span className="w-12 text-center text-sm font-medium text-foreground-950 select-none">
                        {qty}
                      </span>
                      <button
                        type="button"
                        onClick={incQty}
                        aria-label="Aumentar quantidade"
                        className="w-10 h-10 flex items-center justify-center text-foreground-900 hover:bg-background-100 rounded-full cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={qty >= effectiveStock}
                      >
                        <i className="ri-add-line text-[16px]"></i>
                      </button>
                    </div>
                    {effectiveStock > 0 && allOptionsChosen && (
                      <span className="text-[12px] text-foreground-600">
                        {effectiveStock} disponíveis
                      </span>
                    )}
                  </div>
                </div>

              </div>

              {/* Actions */}
              <div className="mt-6 flex items-center gap-2">
                <button
                  type="button"
                  onClick={onAddToCart}
                  disabled={!canPurchase}
                  className="flex-1 h-12 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[15px] font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap inline-flex items-center justify-center gap-2"
                >
                  <i className="ri-shopping-bag-line"></i>
                  {!allOptionsChosen
                    ? "Escolha as opções"
                    : effectiveStock <= 0
                    ? "Esgotado"
                    : "Adicionar ao carrinho"}
                </button>
                <button
                  type="button"
                  onClick={onToggleFavorite}
                  aria-label={liked ? "Salvo" : "Salvar nos favoritos"}
                  className="shrink-0 w-12 h-12 rounded-full border border-background-300 hover:border-foreground-900 text-foreground-900 transition-colors cursor-pointer inline-flex items-center justify-center"
                >
                  <i className={`${liked ? "ri-heart-fill text-primary-500" : "ri-heart-line"} text-[18px]`}></i>
                </button>
              </div>

              {/* Shipping & maker quick row */}
              <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-foreground-700">
                <li className="inline-flex items-center gap-1.5">
                  <i className="ri-truck-line text-foreground-900"></i>
                  Envio em 2-3 dias úteis
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <i className="ri-gift-line text-foreground-900"></i>
                  Embalagem para presente grátis
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <i className="ri-refresh-line text-foreground-900"></i>
                  Trocas em até 30 dias
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tabs / About */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-8 pb-12">
          <div className="border-b border-background-200/70 flex items-center gap-6">
            {([
              ["about", "Detalhes"],
              ["shipping", "Envio"],
              ["returns", "Trocas"],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`relative py-3 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === key
                    ? "text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {label}
                {activeTab === key && (
                  <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-foreground-950"></span>
                )}
              </button>
            ))}
          </div>

          <div className="pt-6 max-w-3xl">
            {activeTab === "about" && (
              <p className="text-[15px] leading-7 text-foreground-800 whitespace-pre-line">
                {product.description ?? "Produto artesanal."}
              </p>
            )}
            {activeTab === "shipping" && (
              <div className="space-y-3 text-[14px] leading-7 text-foreground-800">
                <p>Envio em 2 a 3 dias úteis após a confirmação do pagamento.</p>
                <p>Capitais: 3 a 7 dias. Interior: 5 a 12 dias.</p>
                <p>Código de rastreamento enviado por e-mail.</p>
              </div>
            )}
            {activeTab === "returns" && (
              <div className="space-y-3 text-[14px] leading-7 text-foreground-800">
                <p>Trocas aceitas em até 30 dias após o recebimento.</p>
                <p>Itens personalizados não são elegíveis para troca.</p>
                <p>Lavar à mão. Não usar lava-louças.</p>
              </div>
            )}
          </div>
        </section>

        <ReviewsBlock productId={product.id} rating={rating} count={count} />

        {related.length > 0 && (
          <RelatedProducts products={related} />
        )}

        {recentExcludingCurrent.length > 0 && (
          <RecentlyViewed ids={recentExcludingCurrent} onClear={clearRecent} />
        )}
      </main>

      <Footer />
    </div>
  );
}